import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { User, Mail, Shield, Bell, Globe, Camera, Save, Send } from 'lucide-react';
import { useAgent, useAgents } from '../context/AgentsContext';
import { updateAppUser } from '../lib/api';
import { appendEmailLog, loadEmailLog } from '../lib/skyluxStore';

const AdminSettings = () => {
  const { agentId } = useParams();
  const { refresh, canManageAgents, sessionUserId } = useAgents();
  const agent = useAgent(agentId);
  const canEditProfile = canManageAgents || (!!agent && sessionUserId === agent.id);
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [testTo, setTestTo] = React.useState(agent?.email ?? '');
  const [displayName, setDisplayName] = React.useState(agent?.name ?? '');
  const [nickname, setNickname] = React.useState(agent?.nickname ?? '');
  const [profileEmail, setProfileEmail] = React.useState(agent?.email ?? '');

  React.useEffect(() => {
    setTestTo(agent?.email ?? '');
    setDisplayName(agent?.name ?? '');
    setNickname(agent?.nickname ?? '');
    setProfileEmail(agent?.email ?? '');
  }, [agent?.email, agent?.name, agent?.nickname]);

  const handleSave = async () => {
    if (!agent) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateAppUser(agent.id, {
        displayName: displayName.trim(),
        nickname: nickname.trim() || undefined,
        profileEmail: profileEmail.trim() || undefined,
      });
      await refresh();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const sendDeliverabilityTest = () => {
    const to = testTo.trim();
    if (!to || !agent) {
      window.alert('Set a recipient email.');
      return;
    }
    appendEmailLog({
      id: `em_${Date.now()}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: agent.email,
      to,
      subject: 'Skylux deliverability test (settings)',
      kind: 'test',
      preview: `Demo log entry. Configure SPF/DKIM for ${agent.email} in production.`,
    });
    loadEmailLog();
    window.alert(`Test logged from ${agent.email} to ${to}. See Email center for history.`);
  };

  if (!agent) {
    return (
      <div className="animate-fade">
        <p style={{ color: 'var(--text-muted)' }}>
          No agent selected.{' '}
          <Link to="/admin/agents" style={{ color: 'var(--accent)', fontWeight: 700 }}>
            Add or choose an agent
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade">
      {saved && (
        <div
          style={{
            position: 'fixed',
            top: '100px',
            right: '40px',
            background: 'var(--success)',
            color: 'white',
            padding: '1rem 2rem',
            borderRadius: '0.75rem',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
          }}
        >
          Settings saved.
        </div>
      )}
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '2.25rem', fontWeight: '800', letterSpacing: '-0.025em' }}>Account Settings</h2>
        <p style={{ color: 'var(--text-muted)' }}>
          Profile for <strong>{agent.name}</strong>. Shown on quotes, checkout, and outbound emails.
          {!canManageAgents && ' (Contact admin to change login credentials.)'}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '2.5rem' }}>
        <div style={{ display: 'grid', gap: '0.5rem', alignContent: 'start' }}>
          <SettingsTab icon={<User size={18} />} label="General Profile" active />
          <SettingsTab icon={<Mail size={18} />} label="Email Config" />
          <SettingsTab icon={<Shield size={18} />} label="Security" />
          <SettingsTab icon={<Bell size={18} />} label="Notifications" />
          <SettingsTab icon={<Globe size={18} />} label="Regional settings" />
        </div>

        <div style={{ display: 'grid', gap: '2rem' }}>
          <div style={{ background: 'white', borderRadius: '1.5rem', border: '1px solid var(--border)', padding: '2.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', marginBottom: '3rem' }}>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '2rem',
                    background: 'var(--accent)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.5rem',
                    fontWeight: '800',
                  }}
                >
                  {agent.name[0]}
                </div>
                <button
                  type="button"
                  style={{
                    position: 'absolute',
                    bottom: '-8px',
                    right: '-8px',
                    width: '36px',
                    height: '36px',
                    borderRadius: '12px',
                    background: 'white',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'var(--shadow-md)',
                  }}
                >
                  <Camera size={18} />
                </button>
              </div>
              <div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '800' }}>{agent.name}</h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  <strong>{agent.nickname}</strong> · {agent.email || 'No email set'}
                </p>
              </div>
            </div>

            <form
              style={{ display: 'grid', gap: '2rem' }}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSave();
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '800',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    marginBottom: '0.5rem',
                    display: 'block',
                  }}
                >
                  Full name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '800',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    marginBottom: '0.5rem',
                    display: 'block',
                  }}
                >
                  Nickname (shown in admin)
                </label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="e.g. JohnFly"
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '800',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    marginBottom: '0.5rem',
                    display: 'block',
                  }}
                >
                  Professional email (quotes &amp; checkout)
                </label>
                <input
                  type="email"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  placeholder="agent@yourdomain.com"
                />
              </div>

              {saveError && <p style={{ color: '#b91c1c', margin: 0, fontWeight: 600 }}>{saveError}</p>}

              <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={saving || !canEditProfile}
                  style={{
                    background: 'var(--primary)',
                    color: 'white',
                    padding: '0.75rem 2rem',
                    borderRadius: '0.75rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: canEditProfile ? 1 : 0.6,
                  }}
                >
                  <Save size={18} /> {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
              {!canEditProfile && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
                  Sign in as this agent or use the main admin account to edit this profile.
                </p>
              )}
            </form>
          </div>

          <div style={{ background: 'white', borderRadius: '1.5rem', border: '1px solid var(--border)', padding: '2rem' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: '800', marginBottom: '0.5rem' }}>Test email deliverability</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              Sends a demo log entry as <strong>{agent.email || '—'}</strong>. Use the same flow from <strong>Email</strong> in the
              sidebar for a full log view.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 240px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)', display: 'block', marginBottom: '0.35rem' }}>
                  Recipient
                </label>
                <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              </div>
              <button
                type="button"
                onClick={sendDeliverabilityTest}
                style={{
                  background: 'var(--accent)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.75rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Send size={18} /> Run test
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsTab = ({ icon, label, active = false }: { icon: React.ReactNode; label: string; active?: boolean }) => (
  <button
    type="button"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem 1rem',
      borderRadius: '0.75rem',
      background: active ? 'white' : 'transparent',
      color: active ? 'var(--primary)' : 'var(--text-muted)',
      border: active ? '1px solid var(--border)' : '1px solid transparent',
      fontWeight: active ? '700' : '600',
      textAlign: 'left',
    }}
  >
    {icon}
    <span>{label}</span>
  </button>
);

export default AdminSettings;
